/**
 * Project API — CRUD, summaries, listing.
 */
import { isAxiosError } from 'axios';
import { apiClient, buildUrl, DEFAULT_TENANT_ID } from './apiClient';
import type {
  Project,
  ProjectCreateRequest,
  ProjectUpdateRequest,
  ProjectListParams,
  ProjectSummary,
  ProjectSummaryListResponse,
} from './types';

export async function listProjects(params: ProjectListParams = {}): Promise<Project[]> {
  const search = new URLSearchParams();
  if (params.status) {
    search.set('status', params.status);
  }
  if (params.tenantId) {
    search.set('tenant_id', params.tenantId);
  }
  if (params.search) {
    search.set('search', params.search);
  }
  if (params.includeDeleted) {
    search.set('include_deleted', 'true');
  }
  if (typeof params.limit === 'number') {
    search.set('limit', String(params.limit));
  }
  const query = search.toString() ? `?${search.toString()}` : '';
  try {
    const response = await apiClient.get<{ projects: Project[] }>(buildUrl(`/api/v1/projects${query}`));
    return response.data?.projects ?? [];
  } catch (error) {
    // Backward compatibility: older deployments may not expose /projects,
    // but still expose /projects/summaries. Keep core app usable.
    if (!isAxiosError(error) || error.response?.status !== 404) {
      throw error;
    }
    const fallback = await listProjectSummaries(params);
    return fallback.projects.map((summary) => summary.project);
  }
}

export async function getProjectSummary(projectId: string): Promise<ProjectSummary> {
  const response = await apiClient.get<ProjectSummary>(
    buildUrl(`/api/v1/projects/${projectId}/summary`)
  );
  return response.data;
}

export async function listProjectSummaries(params: ProjectListParams = {}): Promise<ProjectSummaryListResponse> {
  const search = new URLSearchParams();
  if (params.status) {
    search.set('status', params.status);
  }
  if (params.tenantId) {
    search.set('tenant_id', params.tenantId);
  }
  if (params.search) {
    search.set('search', params.search);
  }
  if (params.includeDeleted) {
    search.set('include_deleted', 'true');
  }
  if (typeof params.limit === 'number') {
    search.set('limit', String(params.limit));
  }
  const query = search.toString() ? `?${search.toString()}` : '';
  const response = await apiClient.get<ProjectSummaryListResponse>(
    buildUrl(`/api/v1/projects/summaries${query}`)
  );
  return response.data;
}

export async function getProject(projectId: string): Promise<Project> {
  const response = await apiClient.get<Project>(buildUrl(`/api/v1/projects/${projectId}`));
  return response.data;
}

export async function createProject(payload: ProjectCreateRequest): Promise<Project> {
  const body = {
    tenant_id: payload.tenantId || DEFAULT_TENANT_ID,
    name: payload.name,
    description: payload.description,
    status: payload.status,
    target_schema_id: payload.targetSchemaId,
    source_connector_id: payload.sourceConnectorId,
    destination_connector_id: payload.destinationConnectorId,
    metadata: payload.metadata,
    created_by: payload.createdBy,
  };
  const response = await apiClient.post<Project>(buildUrl('/api/v1/projects'), body);
  return response.data;
}

export async function updateProject(projectId: string, payload: ProjectUpdateRequest): Promise<Project> {
  const body = {
    name: payload.name,
    description: payload.description,
    status: payload.status,
    target_schema_id: payload.targetSchemaId,
    source_connector_id: payload.sourceConnectorId,
    destination_connector_id: payload.destinationConnectorId,
    metadata: payload.metadata,
  };
  const response = await apiClient.patch<Project>(
    buildUrl(`/api/v1/projects/${projectId}`),
    body
  );
  return response.data;
}

export async function deleteProject(projectId: string): Promise<void> {
  await apiClient.delete(buildUrl(`/api/v1/projects/${projectId}`));
}

export async function softDeleteProject(projectId: string): Promise<Project> {
  const response = await apiClient.post<{ project?: Project }>(
    buildUrl(`/api/v1/projects/${projectId}/soft-delete`)
  );
  return response.data?.project as Project;
}

export async function restoreProject(projectId: string): Promise<Project> {
  const response = await apiClient.post<{ project?: Project }>(
    buildUrl(`/api/v1/projects/${projectId}/restore`)
  );
  return response.data?.project as Project;
}
